import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Link } from "@/components/Atoms";
import { StatusBadge } from "@/components/Atoms";
import { SefanoButton } from "@/components/Molecules";
import type { SettingsBillingProps } from "@/components/Templates/Settings/Settings.template.types";
import * as styles from "./SettingsBillingPanel.styles";

type SettingsBillingPanelProps = SettingsBillingProps;

export function SettingsBillingPanel({ billing, isLoading }: SettingsBillingPanelProps) {
  const { t } = useTranslation("settings");

  if (isLoading) {
    return <CircularProgress size={32} />;
  }

  if (!billing) {
    return null;
  }

  const status = billing.isRestricted
    ? { label: t("billing.restricted"), color: "error" as const }
    : billing.hasDebt
      ? { label: t("billing.hasDebt"), color: "warning" as const }
      : { label: t("billing.clear"), color: "success" as const };

  return (
    <Box sx={styles.stack}>
      <Box sx={styles.planCard}>
        <Box sx={styles.planHeader}>
          <Typography sx={styles.planTitle}>
            {billing.hasDebt
              ? t("billing.debt", { amount: billing.outstandingDebtLabel })
              : t("billing.noDebt")}
          </Typography>
          <StatusBadge label={status.label} color={status.color} size="md" />
        </Box>
        <Typography sx={styles.planPrice}>
          {t("billing.totalCommission", { amount: billing.totalCommissionLabel })}
        </Typography>
        {billing.onlinePaymentEnabled ? null : (
          <Typography sx={styles.planNote}>{t("billing.noIban")}</Typography>
        )}
      </Box>

      <Box sx={{ display: "flex" }}>
        <Link to="/billing" sx={{ width: { xs: "100%", md: "auto" } }}>
          <SefanoButton fullWidth sx={{ width: { xs: "100%", md: 190 } }}>
            {t("billing.manage")}
          </SefanoButton>
        </Link>
      </Box>
    </Box>
  );
}
